import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

@Injectable({ providedIn: 'root' })
export class NetworkService {
  private zone = inject(NgZone);

  private readonly onlineSubject = new BehaviorSubject<boolean>(
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : true,
  );

  readonly online$: Observable<boolean> = this.onlineSubject.pipe(distinctUntilChanged());

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      this.initNativeListener();
    } else {
      window.addEventListener('online', () => this.zone.run(() => this.onlineSubject.next(true)));
      window.addEventListener('offline', () => this.zone.run(() => this.onlineSubject.next(false)));
    }
  }

  private async initNativeListener(): Promise<void> {
    try {
      const status = await Network.getStatus();
      this.zone.run(() => this.onlineSubject.next(status.connected));
    } catch {
      // keep current value if the plugin call fails
    }
    Network.addListener('networkStatusChange', (status) => {
      this.zone.run(() => this.onlineSubject.next(status.connected));
    });
  }

  isOnline(): boolean {
    return this.onlineSubject.value;
  }
}
